import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../config/axios';
import ResultadoAnalisis from './ResultadoAnalisis';
import { extraerDatosAnalisis } from '../utils/analisisUtils';

const AnalisisExamenes = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [textoExtraido, setTextoExtraido] = useState('');
  const [usuarioId, setUsuarioId] = useState(null);
  const [perfilCompleto, setPerfilCompleto] = useState(false);

  useEffect(() => {
    // Obtener el ID del usuario del token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUsuarioId(payload.usuarioId);
        
        // Verificar el perfil del usuario
        const verificarPerfil = async () => {
          try {
            const response = await axiosInstance.get(`/usuarios/${payload.usuarioId}`);
            const usuario = response.data;
            
            // Verificar que todos los campos requeridos estén presentes
            const perfilEstaCompleto = !!(
              usuario.telefonos?.movil && 
              usuario.grupoSanguineo && 
              usuario.fechaNacimiento && 
              usuario.genero && 
              usuario.previsionSalud?.tipo && 
              usuario.contactoEmergencia?.nombre && 
              usuario.contactoEmergencia?.telefono
            );
            
            setPerfilCompleto(perfilEstaCompleto);
            
            if (!perfilEstaCompleto) {
              setError('Para realizar un análisis, primero debe completar su perfil con: teléfono móvil, grupo sanguíneo, fecha de nacimiento, género, previsión de salud y contacto de emergencia.');
              // Redirigir al perfil después de 5 segundos
              setTimeout(() => {
                window.location.href = '/perfil';
              }, 5000);
            }
          } catch (error) {
            console.error('Error al verificar perfil:', error);
            setError('Error al verificar el perfil del usuario');
          }
        };
        
        verificarPerfil();
      } catch (error) {
        console.error('Error al decodificar el token:', error);
        setError('Error al identificar al usuario');
      }
    }
  }, []);

  const steps = [
    {
      label: 'Seleccionar Archivo',
      description: 'Selecciona un archivo PDF o imagen de tu examen médico.'
    },
    {
      label: 'Extraer Texto',
      description: 'Extraemos el texto del documento para su análisis.'
    },
    {
      label: 'Analizar Contenido',
      description: 'Analizamos el contenido para proporcionar un diagnóstico.'
    }
  ];

  const handleFileSelect = (event) => {
    if (!perfilCompleto) {
      setError('Para realizar un análisis, primero debe completar su perfil con: teléfono móvil, grupo sanguíneo, fecha de nacimiento, género, previsión de salud y contacto de emergencia.');
      // Redirigir al perfil después de 5 segundos
      setTimeout(() => {
        window.location.href = '/perfil';
      }, 5000);
      return;
    }

    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
        setSelectedFile(file);
        setError(null);
        setActiveStep(1);
        setResultado(null);
        setTextoExtraido('');
      } else {
        setError('Por favor, selecciona un archivo PDF o una imagen');
        setSelectedFile(null);
      }
    }
  };

  const handleExtraerTexto = async () => {
    if (!selectedFile) {
      setError('Por favor, selecciona un archivo primero');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('archivo', selectedFile);

      const response = await axiosInstance.post('/analisis', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.texto) {
        setTextoExtraido(response.data.texto);
        setActiveStep(2);
      } else {
        throw new Error('No se pudo extraer texto del archivo');
      }
    } catch (error) {
      console.error('Error al extraer texto:', error);
      setError(error.response?.data?.error || 'Error al extraer texto del archivo');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalizar = async () => {
    if (!textoExtraido) {
      setError('No hay texto para analizar');
      return;
    }

    if (!usuarioId) {
      setError('No se pudo identificar al paciente');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/analisis/analizar', {
        textoExtraido: textoExtraido
      });

      if (response.data && response.data.analisis) {
        // Extraer datos del análisis usando las mismas funciones que en audio
        const datosAnalisis = extraerDatosAnalisis(response.data.analisis);

        // Asegurarnos de que el resultado tenga la estructura correcta y consistente
        const resultadoFormateado = {
          analisis: response.data.analisis,
          texto: textoExtraido,
          transcripcion: textoExtraido,
          ...datosAnalisis
        };

        setResultado(resultadoFormateado);
        setActiveStep(3);

        // Guardar en el historial del paciente con la información completa
        try {
          const casoData = {
            paciente: usuarioId,
            tipo: 'examen',
            resultado: resultadoFormateado.analisis,
            transcripcion: resultadoFormateado.texto,
            textoExtraido: resultadoFormateado.texto,
            nivelUrgencia: datosAnalisis.nivelUrgencia,
            sintomas: datosAnalisis.sintomas || [],
            signosAlarma: datosAnalisis.signosAlarma || [],
            diagnosticoPreliminar: datosAnalisis.diagnosticoPreliminar || '',
            recomendaciones: datosAnalisis.recomendaciones || [],
            centroRecomendado: datosAnalisis.centroRecomendado || '',
            estado: 'pendiente',
            fechaCreacion: new Date().toISOString()
          };

          console.log('Guardando caso:', casoData);
          
          try {
            await axiosInstance.post('/casos', casoData);
          } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.error?.includes('completar su perfil')) {
              setError('Para realizar un análisis, primero debe completar su perfil con: teléfono móvil, grupo sanguíneo, fecha de nacimiento, género, previsión de salud y contacto de emergencia.');
              // Redirigir al perfil después de 5 segundos
              setTimeout(() => {
                window.location.href = '/perfil';
              }, 5000);
              return;
            }
            throw error;
          }
        } catch (historialError) {
          console.error('Error al guardar en historial:', historialError);
          setError('Error al guardar el análisis en el historial');
        }
      } else {
        throw new Error('No se recibió un análisis válido del servidor');
      }
    } catch (error) {
      console.error('Error al analizar el texto:', error);
      setError(error.response?.data?.error || 'Error al analizar el texto');
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setResultado(null);
    setError(null);
    setActiveStep(0);
    setTextoExtraido('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Análisis de Exámenes
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                <Typography>{step.description}</Typography>
                <Box sx={{ mt: 2 }}>
                  {index === 0 && (
                    <>
                      <input
                        accept="application/pdf,image/*"
                        style={{ display: 'none' }}
                        id="upload-file"
                        type="file"
                        onChange={handleFileSelect}
                        disabled={!perfilCompleto}
                      />
                      <label htmlFor="upload-file">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          disabled={loading || !perfilCompleto}
                        >
                          Seleccionar Archivo
                        </Button>
                      </label>
                    </>
                  )}

                  {index === 1 && (
                    <Button
                      variant="contained"
                      onClick={handleExtraerTexto}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          Extrayendo texto...
                        </>
                      ) : (
                        'Extraer Texto'
                      )}
                    </Button>
                  )}

                  {index === 2 && (
                    <>
                      {textoExtraido && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Texto Extraído:
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {textoExtraido}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                      <Button
                        variant="contained"
                        onClick={handleAnalizar}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            Analizando...
                          </>
                        ) : (
                          'Analizar Contenido'
                        )}
                      </Button>
                    </>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {selectedFile && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              Archivo seleccionado: {selectedFile.name}
            </Typography>
            <IconButton size="small" onClick={clearFile}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {resultado && (
          <Box sx={{ mt: 3 }}>
            <ResultadoAnalisis resultado={resultado} tipo="examen" />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AnalisisExamenes; 